/**
 * Symmetric-margin op. The staged
 * resolve→preview→commit slice for a NEW structure grown/shrunk by one signed
 * cm margin, built on the shared pipeline core. cm→mm happens at the single
 * branded choke-point; `Validated`/`Previewed` are unforgeable so a mutation
 * cannot skip a stage, and commit consumes only what preview computed.
 */
import {
    cmToMmChokepoint,
    resolveSourceForNewStructure,
    previewSingleCall,
    commitComputed,
    MAX_ABS_MARGIN_CM,
    type Brand,
    type Mm,
    type ComputedGeometry,
    type OpDeps,
    type ValidateResult,
    type CommitResult,
} from '../core';
import type { ApplySymmetricMarginInput } from '../../contract';

export type SymmetricMarginInput = ApplySymmetricMarginInput;

export type SymmetricMarginDeps = OpDeps<ComputedGeometry> & {
    readonly geometry: {
        applySymmetricMargin(
            sourceRoiNumber: number,
            marginMm: Mm
        ): Promise<ComputedGeometry>;
    };
};

/* ── Stage 1: resolve (target + units + invariants) ── */

interface ValidatedData {
    readonly sourceRoiNumber: number;
    readonly sourceVolumeCm3: number;
    readonly marginMm: Mm;
    readonly newRoi: string;
}
/** Only `resolveSymmetricMargin` mints this (the brand is unforgeable
 *  without an explicit cast) — preview cannot be skipped. */
export type Validated = Brand<ValidatedData, 'Validated'>;

export function resolveSymmetricMargin(
    input: SymmetricMarginInput,
    deps: SymmetricMarginDeps
): ValidateResult<Validated> {
    // Fail-fast on the pure, dependency-free numeric invariant first.
    const m = input.marginCm;
    if (!Number.isFinite(m) || m === 0) {
        return {
            kind: 'invalid',
            reason: 'margin must be a non-zero finite number of centimetres',
        };
    }
    if (Math.abs(m) > MAX_ABS_MARGIN_CM) {
        return {
            kind: 'invalid',
            reason:
                `margin ${m} cm is outside the safe ±${MAX_ABS_MARGIN_CM} cm ` +
                'range (possible cm/mm mix-up)',
        };
    }

    const resolved = resolveSourceForNewStructure(
        input.roi,
        input.newRoi,
        deps.structures,
        'margin',
        'expand or contract'
    );
    if (resolved.kind !== 'source') {
        return resolved;
    }

    const validated = {
        sourceRoiNumber: resolved.source.roiNumber,
        sourceVolumeCm3: resolved.source.volumeCm3,
        marginMm: cmToMmChokepoint(input.marginCm),
        newRoi: input.newRoi,
    } as Validated;
    return { kind: 'validated', validated };
}

/* ── Stage 2: preview (compute geometry ONCE) ── */

interface PreviewedData {
    readonly computed: ComputedGeometry;
    readonly newRoi: string;
    readonly volumeDeltaCm3: number;
}
/** Only `previewSymmetricMargin` mints this; `commit` accepts only this, so
 *  the applied geometry IS the previewed geometry (no recompute path). */
export type Previewed = Brand<PreviewedData, 'Previewed'>;

export type PreviewResult =
    | { readonly kind: 'previewed'; readonly previewed: Previewed }
    | { readonly kind: 'failed'; readonly reason: string };

export async function previewSymmetricMargin(
    validated: Validated,
    deps: SymmetricMarginDeps
): Promise<PreviewResult> {
    const r = await previewSingleCall(
        validated,
        (v) =>
            deps.geometry.applySymmetricMargin(v.sourceRoiNumber, v.marginMm),
        deps.geometry.releaseComputed,
        'the resulting structure would be empty'
    );
    return r.kind === 'failed'
        ? r
        : { kind: 'previewed', previewed: r.previewed as Previewed };
}

/* ── Stage 3: commit (write the ALREADY-computed geometry to a NEW ROI) ── */

export async function commitSymmetricMargin(
    previewed: Previewed,
    deps: SymmetricMarginDeps
): Promise<CommitResult> {
    return commitComputed(previewed, deps.commit.createNewStructure);
}
